import {
	CITY_MAX_CHARS,
	COUNTRY_WITH_STATE_OR_PROVINCES,
	CREDIT_CARDS_TYPES,
	FIRST_NAME_LIMIT_CHARS,
	LAST_NAME_LIMIT_CHARS,
	NAME_POLICY_REGEX,
	PAYMENT_METHODS,
} from "app/constants";
import { determineCardType, validateCreditCardData } from "app/utils/creditCardUtils";
import { string, object, array, boolean, number } from "yup";
import { validatePhoneIntl } from "app/utils/utils";
import { isEmpty } from "lodash";
import forOwn from "lodash/forOwn";

export const dependantPaymentCardFields = ["expiryMonth", "expiryYear", "cvc", "number"];
export const dependantPassengersNameFields = ["lastName", "firstName"];

const CREDIT_CARD_ERROR_MAP = {
	cvc: "CVC incorrect",
	holderName: "holderName incorrect",
	month: "Mois incorrect",
	year: "Annee incorrecte",
	expiryYear: "Année expiration incorrecte",
	expiryMonth: "Mois expiration incorrecte",
	number: "Numero de carte incorrecte",
	luhn: "Validation Lhun incorrecte",
};

export const defaultValues = {
	contact: {
		"honorific-prefix": "",
		"family-name": "",
		"given-name": "",
		"address-line1": "",
		"address-line2": "",
		"postal-code": "",
		"address-level2": "", // city
		"country-name": "",
		state: "",
		phone: "",
		email: "",
	},
	passengers: [
		{
			type: "adult",
			index: 1,
			name: {
				gender: "",
				lastName: "",
				firstName: "",
			},
			birthdate: { day: "", month: "", year: "" },
			frequentFlyerTravellerCardNumber: "",
		},
		{
			type: "adult",
			index: 2,
			name: {
				gender: "",
				lastName: "",
				firstName: "",
			},
			birthdate: { day: "", month: "", year: "" },
			frequentFlyerTravellerCardNumber: "",
		},
	],
	coupon: "",
	insurance: "",
	terms: false,
	payment: {
		mode: "",
		type: "",
	},
	reuseContactInfo: false,
};

const hasNonLatinCharacters = value =>
	typeof value === "string" && NAME_POLICY_REGEX.NON_LATIN_CHAR.test(value);

const contactSchemaShape = {
	"family-name": string()
		.required({ id: "error.firstName.required" })
		.min(FIRST_NAME_LIMIT_CHARS, { id: "error.firstName.minlength" })
		.test(
			"name-policy-char",
			{ id: "error.firstName.invalid" },
			value =>
				!(NAME_POLICY_REGEX.SPECIAL_CHAR.test(value) || NAME_POLICY_REGEX.DIGIT.test(value))
		)
		.test(
			"latin-characters-only",
			{ id: "error.lastName.only.latin.characters" },
			value => !hasNonLatinCharacters(value)
		),

	"given-name": string()
		.required({ id: "error.lastName.required" })
		.min(LAST_NAME_LIMIT_CHARS, { id: "error.lastName.minlength" })
		.test(
			"name-policy-char",
			{ id: "error.lastName.invalid" },
			value =>
				!(NAME_POLICY_REGEX.SPECIAL_CHAR.test(value) || NAME_POLICY_REGEX.DIGIT.test(value))
		)
		.test(
			"latin-characters-only",
			{ id: "error.firstName.only.latin.characters" },
			value => !hasNonLatinCharacters(value)
		),
	// La validation automatique des e-mail avec Yup ne fonctionne pas correctement. Il y a un issue ouvert à ce sujet depuis 2019 https://github.com/jquense/yup/issues/507#issuecomment-765799429.
	// il faut utiliser le regex
	email: string()
		.required({ id: "error.email.required" })
		.test("email", { id: "error.email.format" }, value =>
			/^(([^<>()\\.,;:\s@"]+(\.[^<>()\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
				value
			)
		),
	phone: string()
		.required({ id: "error.phone.required" })
		.test("phone-check", { id: "error.phone.invalid" }, value => validatePhoneIntl(value)),
};

const contactSchema = object().shape({
	...contactSchemaShape,
	"honorific-prefix": string().required({ id: "error.gender.required" }),
	"address-line1": string()
		.required({ id: "error.address.line1.required" })
		.test(
			"address-line1-policy-char",
			{ id: "error.address.invalid" },
			value => !NAME_POLICY_REGEX.SPECIAL_CHAR.test(value)
		),
	"address-line2": string().test(
		"address-line2-policy-char",
		{ id: "error.address.invalid" },
		value => !NAME_POLICY_REGEX.SPECIAL_CHAR.test(value)
	),
	"postal-code": string()
		.required({ id: "error.address.postCode.required" })
		.test(
			"postal-code-policy-char",
			{ id: "error.address.invalid" },
			value => !NAME_POLICY_REGEX.SPECIAL_CHAR.test(value)
		),
	"address-level2": string()
		.required({ id: "error.address.city.required" })
		.max(CITY_MAX_CHARS, { id: "error.address.city.minlength" })
		.test(
			"address-level2-policy-char",
			{ id: "error.address.invalid" },
			value => !NAME_POLICY_REGEX.SPECIAL_CHAR.test(value)
		),
	state: string().test("state-required", { id: "error.address.state.required" }, function(
		value,
		context
	) {
		const country = context?.parent?.["country-name"];
		if (COUNTRY_WITH_STATE_OR_PROVINCES.includes(country)) {
			return Boolean(value);
		}
		return true;
	}),
	"country-name": string().required({ id: "error.address.country.required" }),
});

const passengersSchema = (dateOfBirthRequired, fidelityCardNumberValidation) =>
	array().of(
		object().shape({
			name: object().shape({
				gender: string().required({ id: "error.gender.required" }),
				lastName: string()
					.required({ id: "error.lastName.required" })
					.min(LAST_NAME_LIMIT_CHARS, { id: "error.lastName.minlength" })
					.test("unique-full-name", { id: "error.passengers.unique.invalid" }, function(
						value,
						context
					) {
						const passengers =
							context?.from[context?.from?.length - 1]?.value?.passengers;

						// Accès aux autres valeurs du schéma via context.parent
						const { firstName, lastName } = context?.parent;
						const path = context.path;
						const index = Number(path.charAt(path.indexOf("[") + 1));
						const type = passengers[`${index}`].type;
						//  Vérification de l'unicité
						return !passengers.some((passenger, idx) => {
							if (
								typeof passenger?.name?.firstName === "string" &&
								typeof passenger?.name?.lastName === "string" &&
								typeof firstName === "string" &&
								typeof lastName === "string"
							) {
								return (
									idx !== index &&
									passenger.name?.firstName.toLowerCase() ===
										firstName.toLowerCase() &&
									passenger.name.lastName.toLowerCase() ===
										lastName.toLowerCase() &&
									passenger.type === type &&
									passenger.index < index + 1
								);
							}
							return false;
						});
					})
					.test(
						"name-policy-char",
						{ id: "error.lastName.invalid" },
						value =>
							!(
								NAME_POLICY_REGEX.SPECIAL_CHAR.test(value) ||
								NAME_POLICY_REGEX.DIGIT.test(value)
							)
					)
					.test(
						"latin-characters-only",
						{ id: "error.lastName.only.latin.characters" },
						value => !hasNonLatinCharacters(value)
					),
				firstName: string()
					.required({ id: "error.firstName.required" })
					.min(FIRST_NAME_LIMIT_CHARS, { id: "error.firstName.minlength" })
					.test("unique-full-name", { id: "error.passengers.unique.invalid" }, function(
						value,
						context
					) {
						const passengers =
							context?.from[context?.from?.length - 1]?.value?.passengers;

						// Accès aux autres valeurs du schéma via context.parent
						const { firstName, lastName } = context?.parent;
						const path = context.path;
						const index = Number(path.charAt(path.indexOf("[") + 1));
						const type = passengers[`${index}`].type;
						//  Vérification de l'unicité
						return !passengers.some((passenger, idx) => {
							if (
								typeof passenger?.name?.firstName === "string" &&
								typeof passenger?.name?.lastName === "string" &&
								typeof firstName === "string" &&
								typeof lastName === "string"
							) {
								return (
									idx !== index &&
									passenger.name.firstName.toLowerCase() ===
										firstName.toLowerCase() &&
									passenger.name.lastName.toLowerCase() ===
										lastName.toLowerCase() &&
									passenger.type === type &&
									passenger.index < index + 1
								);
							}
							return false;
						});
					})
					.test(
						"name-policy-char",
						{ id: "error.firstName.invalid" },
						value =>
							!(
								NAME_POLICY_REGEX.SPECIAL_CHAR.test(value) ||
								NAME_POLICY_REGEX.DIGIT.test(value)
							)
					)
					.test(
						"latin-characters-only",
						{ id: "error.firstName.only.latin.characters" },
						value => !hasNonLatinCharacters(value)
					),
			}),
			birthdate: dateOfBirthRequired
				? object().shape({
						day: string().required({ id: "error.birthdate.day" }),
						month: string().required({ id: "error.birthdate.month" }),
						year: string().required({ id: "error.birthdate.year" }),
				  })
				: object().shape({}),
			...(fidelityCardNumberValidation && {
				frequentFlyerTravellerCardNumber: fidelityCardNumberValidation,
			}),
		})
	);
const insuranceSchema = string().required({ id: "error.payment.insurance.required" });

const termsSchema = boolean().oneOf([true], { id: "error.must.accept.terms" });

export const paymentSchema = (
	selectedPaymentTypeValue,
	acceptedCreditCardTypes,
	cartAmount,
	showAmountToPay,
	disableCreditCardFormValidation
) => {
	let paymentValidators = {};

	if (disableCreditCardFormValidation) {
		paymentValidators = {
			card: object().shape({
				amount: showAmountToPay
					? number()
							.positive({ id: "error.payment.card.amount.format" })
							.min(1, { id: "error.payment.card.amount.format" })
							.max(cartAmount, { id: "error.payment.card.amount.lessThan" })
					: undefined,
			}),
		};
	} else if (selectedPaymentTypeValue === PAYMENT_METHODS.CARD_CSE) {
		paymentValidators = {
			card: object().shape({
				amount: showAmountToPay
					? number()
							.positive({ id: "error.payment.card.amount.format" })
							.min(1, { id: "error.payment.card.amount.format" })
							.max(cartAmount, { id: "error.payment.card.amount.lessThan" })
					: undefined,
				holderName: string().required({
					id: "error.payment.card.holder.name.required",
				}),
				number: string()
					.test("number-required", { id: "error.payment.card.number.required" }, function(
						value
					) {
						if (determineCardType(value) !== CREDIT_CARDS_TYPES.MAESTRO && !value) {
							return false;
						}
						return true;
					})
					.test(
						"is-card-type-accepted",
						{ id: "error.payment.credit.card.type.not.accepted.new" },
						function(value) {
							const creditCardType = determineCardType(value);
							return acceptedCreditCardTypes?.includes(creditCardType);
						}
					)
					.test(
						"valid-credit-card",
						{ id: "error.payment.credit.card.invalid" },
						function(value) {
							const payment = this.parent;
							const creditCardData = {
								number: value,
								cvc: payment?.cvc,
								expiryMonth: payment?.expiryMonth,
								expiryYear: "20" + payment?.expiryYear,
								holderName: payment?.holderName,
							};

							let adyenErrors = validateCreditCardData(creditCardData);
							let errors = {};

							forOwn(adyenErrors, (value, key) => {
								if (value === false) {
									errors[key] = CREDIT_CARD_ERROR_MAP[key];
								}
							});

							return isEmpty(errors);
						}
					),
				expiryMonth: string()
					.required({
						id: "error.payment.card.expiryMonth.required",
					})
					.test(
						"valid-credit-card",
						{ id: "error.payment.credit.card.invalid" },
						function() {
							const payment = this.parent;
							const creditCardData = {
								number: payment?.number,
								cvc: payment?.cvc,
								expiryMonth: payment?.expiryMonth,
								expiryYear: "20" + payment?.expiryYear,
								holderName: payment?.holderName,
							};

							let adyenErrors = validateCreditCardData(creditCardData);

							let errors = {};

							forOwn(adyenErrors, (value, key) => {
								if (value === false) {
									errors[key] = CREDIT_CARD_ERROR_MAP[key];
								}
							});

							return isEmpty(errors);
						}
					),
				expiryYear: string()
					.required({
						id: "error.payment.card.expiryYear.required",
					})
					.test(
						"valid-credit-card",
						{ id: "error.payment.credit.card.invalid" },
						function() {
							const payment = this.parent;
							const creditCardData = {
								number: payment?.number,
								cvc: payment?.cvc,
								expiryMonth: payment?.expiryMonth,
								expiryYear: "20" + payment?.expiryYear,
								holderName: payment?.holderName,
							};

							let adyenErrors = validateCreditCardData(creditCardData);
							let errors = {};

							forOwn(adyenErrors, (value, key) => {
								if (value === false) {
									errors[key] = CREDIT_CARD_ERROR_MAP[key];
								}
							});

							return isEmpty(errors);
						}
					),
				cvc: string()
					.test("valid-cvc", { id: "error.payment.card.cvc.required" }, function(value) {
						const shouldValidateCVC =
							determineCardType(this.parent?.number) !== CREDIT_CARDS_TYPES.MAESTRO;

						if (shouldValidateCVC) {
							return !isEmpty(value);
						}
						return true;
					})
					.test(
						"valid-credit-card",
						{ id: "error.payment.credit.card.invalid" },
						function() {
							const payment = this.parent;
							const creditCardData = {
								number: payment?.number,
								cvc: payment?.cvc,
								expiryMonth: payment?.expiryMonth,
								expiryYear: "20" + payment?.expiryYear,
								holderName: payment?.holderName,
							};

							let adyenErrors = validateCreditCardData(creditCardData);
							let errors = {};

							forOwn(adyenErrors, (value, key) => {
								if (value === false) {
									errors[key] = CREDIT_CARD_ERROR_MAP[key];
								}
							});

							return isEmpty(errors);
						}
					), //
			}),
		};
	} else if (selectedPaymentTypeValue === PAYMENT_METHODS.CARD) {
		paymentValidators = {
			card: object().shape({
				amount: showAmountToPay
					? number()
							.required({ id: "error.payment.card.amount.required" })
							.positive({ id: "error.payment.card.amount.format" })
							.min(1, { id: "error.payment.card.amount.format" })
							.max(cartAmount, { id: "error.payment.card.amount.lessThan" })
					: undefined,
				// la validation des infos cb est faite dans validateForm de PaymentContainer
			}),
		};
	} else if (selectedPaymentTypeValue === PAYMENT_METHODS.IDEAL) {
		paymentValidators = {
			issuerId: string().required({ id: "error.payment.issuerId.required" }),
		};
	} else if (selectedPaymentTypeValue === PAYMENT_METHODS.SWISS_BILLING) {
		paymentValidators = {
			swissBilling: object().shape({
				birthdate: object().shape({
					day: string().required({ id: "error.birthdate.day" }),
					month: string().required({ id: "error.birthdate.month" }),
					year: string().required({ id: "error.birthdate.year" }),
				}),
				invoiceType: string().required({ id: "error.payment.invoiceType.required" }),
			}),
		};
	}
	if (cartAmount > 0) {
		return object().shape(paymentValidators);
	}
	return object().shape({});
};

export const validationSchema = ({
	selectedPaymentTypeValue,
	acceptedCreditCardTypes,
	cartAmount,
	dateOfBirthRequired,
	showAmountToPay,
	disableInsurances,
	disablePassengers,
	isOperatorMode,
	fidelityCardNumberValidation,
}) =>
	object().shape({
		contact: isOperatorMode ? object().shape(contactSchemaShape) : contactSchema,
		...(disablePassengers || isOperatorMode
			? {}
			: { passengers: passengersSchema(dateOfBirthRequired, fidelityCardNumberValidation) }),
		insurance: isOperatorMode || disableInsurances ? null : insuranceSchema,
		terms: isOperatorMode ? null : termsSchema,
		payment: paymentSchema(
			selectedPaymentTypeValue,
			acceptedCreditCardTypes,
			cartAmount,
			showAmountToPay,
			isOperatorMode
		),
	});
